import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { AuthContext } from "../../../../helpers/AuthContext";
import {
  DEFAULT_TIME_ZONE,
  formatDateTimeInTimeZone,
  getBrowserTimeZone,
  isValidTimeZone,
  listSupportedTimeZones,
  normalizeTimeZone,
} from "../../../../utils/timezone";

export default function CompanyTimezoneTab({ instanceUniqueId }) {
  const { setActiveInstance, setActiveInstanceTimezone } = useContext(AuthContext);
  const [instanceId, setInstanceId] = useState(null);
  const [timezone, setTimezone] = useState(DEFAULT_TIME_ZONE);
  const [savedTimezone, setSavedTimezone] = useState(DEFAULT_TIME_ZONE);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
  const headers = useMemo(
    () => ({ headers: { accessToken: localStorage.getItem("accessToken") } }),
    []
  );
  const browserTimeZone = useMemo(() => getBrowserTimeZone(), []);
  const timeZoneOptions = useMemo(() => listSupportedTimeZones(), []);

  useEffect(() => {
    if (!instanceUniqueId) return;

    let cancelled = false;

    axios
      .get(`${SERVER}/instances/byinstanceUniqueId/${instanceUniqueId}`, headers)
      .then((res) => {
        if (cancelled) return;
        setInstanceId(res.data?.id || null);
      })
      .catch(() => {
        if (cancelled) return;
        setInstanceId(null);
      });

    return () => {
      cancelled = true;
    };
  }, [SERVER, headers, instanceUniqueId]);

  useEffect(() => {
    if (!instanceId) return;

    let cancelled = false;
    setLoading(true);
    setError("");

    axios
      .get(`${SERVER}/instances/${instanceId}/timezone`, headers)
      .then((res) => {
        if (cancelled) return;
      const next = normalizeTimeZone(res.data?.timezone, DEFAULT_TIME_ZONE);
      setTimezone(next);
      setSavedTimezone(next);
      setActiveInstanceTimezone?.(next);
      })
      .catch((loadError) => {
        if (cancelled) return;
        setError(
          loadError?.response?.data?.message || "Could not load company timezone."
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [SERVER, headers, instanceId]);

  const previewText = useMemo(
    () =>
      formatDateTimeInTimeZone(new Date().toISOString(), {
        timeZone: timezone,
        includeTimeZoneName: true,
      }),
    [timezone]
  );

  const saveTimezone = async () => {
    if (!instanceId || !isValidTimeZone(timezone)) return;
    setSaving(true);
    setError("");

    try {
      const res = await axios.put(
        `${SERVER}/instances/${instanceId}/timezone`,
        { timezone },
        headers
      );
      const next = normalizeTimeZone(res.data?.timezone, DEFAULT_TIME_ZONE);
      setTimezone(next);
      setSavedTimezone(next);
      setActiveInstance((current) => (current ? { ...current, timezone: next } : current));
      setActiveInstanceTimezone?.(next);
    } catch (saveError) {
      setError(
        saveError?.response?.data?.message || "Could not save company timezone."
      );
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = timezone !== savedTimezone;

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Company Timezone
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Choose the timezone Elite Core should use for company-level timestamps.
            Date-only business values like in-hands dates stay unchanged.
          </Typography>
        </Box>

        {browserTimeZone && browserTimeZone !== timezone ? (
          <Alert severity="info">
            Browser timezone detected: <strong>{browserTimeZone}</strong>
          </Alert>
        ) : null}

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Autocomplete
          options={timeZoneOptions}
          loading={loading}
          value={timezone}
          onChange={(_event, value) => {
            setTimezone(normalizeTimeZone(value, DEFAULT_TIME_ZONE));
          }}
          onInputChange={(_event, value, reason) => {
            if (reason === "input") setTimezone(value);
          }}
          freeSolo
          renderInput={(params) => (
            <TextField
              {...params}
              label="Company Timezone"
              helperText="Use a valid IANA timezone like America/Edmonton or America/New_York."
              error={Boolean(timezone) && !isValidTimeZone(timezone)}
            />
          )}
        />

        <Box>
          <Typography variant="caption" color="text.secondary">
            Preview
          </Typography>
          <Typography variant="body2">{previewText || "—"}</Typography>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            onClick={saveTimezone}
            disabled={!hasChanges || !isValidTimeZone(timezone) || saving || loading}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Save Timezone
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
